import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { ArrowRight, QrCode, Zap, Smartphone, Share2, Sparkles, Wand2, CheckCircle2, Camera, Music, Disc, Film, Heart, Infinity as InfinityIcon, Star } from "lucide-react";

const ICONS = { Zap, QrCode, Smartphone, Share2, Sparkles, Wand2, Music, Disc, Film, Heart, Infinity: InfinityIcon, Star, Camera, CheckCircle2 };

function ExperiencePage({ section, defaultImage, accent = "tech", pageMeta }) {
  if (!section) return <div className="pt-40 text-center text-white/40">Loading...</div>;

  const isTech = accent === "tech";

  return (
    <div className="pt-24" data-testid={pageMeta.testId}>
      <SEO title={pageMeta.title} description={pageMeta.description} path={pageMeta.path} />

      {/* Hero */}
      <section className={`relative ${isTech ? "bg-gradient-to-b from-[#040404] via-[#0a0a0a] to-[#050505]" : "bg-[#0a0805]"} overflow-hidden`}>
        {isTech && (
          <div className="absolute inset-0 opacity-[0.05]" style={{
            backgroundImage: "linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)",
            backgroundSize: "60px 60px" }} />
        )}
        {!isTech && (
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse at 30% 20%, rgba(212,175,55,0.10), transparent 60%)" }} />
        )}
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          <div className="eyebrow">{section.eyebrow || "Signature Experience"}</div>
          <h1 className="font-serif text-5xl md:text-7xl font-light mt-6 tracking-tight leading-[1.05] max-w-4xl mx-auto">
            {section.title}
          </h1>
          {section.subtitle && (
            <p className="mt-6 text-[#D4AF37] text-lg md:text-xl italic font-light max-w-3xl mx-auto">
              {section.subtitle}
            </p>
          )}
        </div>
      </section>

      {/* Image */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 -mt-12 md:-mt-16">
        <div className="relative overflow-hidden hover-zoom">
          <img src={section.image_url || defaultImage} alt={section.title}
            className="w-full h-[60vh] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        </div>
      </section>

      {/* Description */}
      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="text-white/80 text-lg leading-relaxed">{section.description}</p>
        {section.marketing_highlight && (
          <div className="mt-12 border-l-2 border-[#D4AF37] pl-6 py-2 text-left max-w-2xl mx-auto">
            <p className="font-serif text-2xl text-[#D4AF37] italic leading-snug">
              {section.marketing_highlight}
            </p>
          </div>
        )}
      </section>

      {/* Benefits */}
      <section className="max-w-[1200px] mx-auto px-6 py-16">
        <div className="eyebrow text-center">What Makes It Special</div>
        <h2 className="font-serif text-4xl font-light text-center mt-4 mb-16">A signature you'll <em className="text-[#D4AF37] not-italic">never forget</em></h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
          {(section.benefits || []).map((b, i) => {
            const Icon = ICONS[b.icon] || Sparkles;
            return (
              <div key={i} className="text-center group">
                <div className="w-16 h-16 mx-auto flex items-center justify-center border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37] transition-all duration-500 group-hover:bg-[#D4AF37] group-hover:text-black group-hover:rotate-6">
                  <Icon size={24} />
                </div>
                <p className="mt-5 text-white/85 leading-relaxed">{b.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="eyebrow">{pageMeta.ctaEyebrow}</div>
        <h2 className="font-serif text-4xl md:text-5xl font-light mt-4">
          Let's create <em className="text-[#D4AF37] not-italic">something unforgettable</em>.
        </h2>
        <p className="text-white/60 mt-5 max-w-xl mx-auto">
          A short, honest chat is the easiest first step. We'd love to hear your story.
        </p>
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          <Link to="/contact" className="btn-gold">Say Hello <ArrowRight size={14}/></Link>
          <Link to="/book" className="btn-ghost">Book a 15-min Chat</Link>
        </div>
      </section>
    </div>
  );
}

export function SnapPicServicePage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/homepage").then(r => setData(r.data.snappic_section)).catch(() => {}); }, []);
  return <ExperiencePage section={data} accent="tech"
    defaultImage="https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&w=1600&q=80"
    pageMeta={{
      title: "SnapPic — Instant Photo Sharing for Weddings",
      description: "Your wedding photos delivered instantly. SnapPic is Elite Pixels' QR code instant photo experience that lets guests view and share photos in real time.",
      path: "/services/snappic",
      ctaEyebrow: "Want this at your wedding?",
      testId: "snappic-service-page",
    }} />;
}

export function StorySongServicePage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/homepage").then(r => setData(r.data.story_song_section)).catch(() => {}); }, []);
  return <ExperiencePage section={data} accent="emotional"
    defaultImage="https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=1600&q=80"
    pageMeta={{
      title: "Your Story Song — A Custom Wedding Song by Elite Pixels",
      description: "We transform your love story into a custom song featured in your wedding film — your story, your emotions, your song.",
      path: "/services/story-song",
      ctaEyebrow: "Hear what your story sounds like",
      testId: "story-song-service-page",
    }} />;
}
